import { Controller } from "@hotwired/stimulus"

// Autocomplete picker for schools. As the user types, it queries the server
// and shows a shrinking list of matches; choosing one sets the hidden
// school_id that the form submits.
export default class extends Controller {
  static targets = ["input", "hidden", "results"]
  static values = { url: String }

  connect() {
    this.timeout = null
    this.onOutsideClick = this.onOutsideClick.bind(this)
    document.addEventListener("click", this.onOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onOutsideClick)
    if (this.timeout) clearTimeout(this.timeout)
  }

  search() {
    // Typing invalidates any previous selection until the user picks again,
    // so we never submit a school_id that doesn't match the visible text.
    this.hiddenTarget.value = ""

    const query = this.inputTarget.value.trim()
    if (this.timeout) clearTimeout(this.timeout)

    if (query.length < 2) {
      this.clearResults()
      return
    }

    this.timeout = setTimeout(() => this.fetchResults(query), 200)
  }

  async fetchResults(query) {
    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) return
      this.renderResults(await response.json())
    } catch (error) {
      // Network/parse failure: leave the list closed rather than crash.
    }
  }

  renderResults(schools) {
    this.resultsTarget.innerHTML = ""

    if (schools.length === 0) {
      const empty = document.createElement("li")
      empty.textContent = "No matching schools"
      empty.classList.add("school-search-empty")
      this.resultsTarget.appendChild(empty)
    } else {
      schools.forEach((school) => {
        const option = document.createElement("li")
        option.textContent = school.label
        option.classList.add("school-search-option")
        option.addEventListener("click", () => this.choose(school.id, school.label))
        this.resultsTarget.appendChild(option)
      })
    }

    this.resultsTarget.hidden = false
  }

  choose(id, label) {
    this.hiddenTarget.value = id
    this.inputTarget.value = label
    this.clearResults()
  }

  clearResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.hidden = true
  }

  onOutsideClick(event) {
    if (!this.element.contains(event.target)) this.clearResults()
  }
}
